import {
  Button, Card, DataTable, EmptyState, Permissions, Row, Select, StatusBadge, usePermission, useApi,
  useClampedPage, useI18n, type Column, type TenantProfileResponse, type TenantStatus,
} from '@daghfal/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const STATUSES: TenantStatus[] = ['Trial', 'Active', 'PastDue', 'Suspended', 'Archived'];

/** The tenant list, searchable and filterable by status (FR-PLT-03). */
export function Tenants() {
  const api = useApi();
  const { t, formatDate, formatNumber } = useI18n();
  const navigate = useNavigate();
  const canCreate = usePermission(Permissions.platform.createTenants);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const tenants = useQuery({
    queryKey: ['platform', 'tenants', { search, status, page }],
    queryFn: () => api.platform.listTenants({ search, status, page, pageSize: 25 }),
    // Without this the table empties on every keystroke and the page height jumps about.
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, tenants.data?.totalPages);

  const columns: Column<TenantProfileResponse>[] = [
    {
      key: 'name',
      header: t('tenants.column.name'),
      // The row's link carries the tenant's name, so it is announced as "open Nuva Kids" rather than as a
      // bare "open" repeated down the column.
      render: (tenant) => (
        <Link className="dg-row-button" to={`/tenants/${tenant.id}`}>{tenant.nameAr}</Link>
      ),
    },
    { key: 'slug', header: t('tenants.column.slug'), render: (tenant) => <code>{tenant.slug}</code> },
    { key: 'status', header: t('tenants.column.status'), render: (tenant) => <StatusBadge status={tenant.status} /> },
    { key: 'type', header: t('tenants.column.type'), render: (tenant) => t(`tenants.new.type.${tenant.type}`) },
    { key: 'children', header: t('tenants.column.children'), numeric: true, render: (tenant) => formatNumber(tenant.activeChildren) },
    { key: 'staff', header: t('tenants.column.staff'), numeric: true, render: (tenant) => formatNumber(tenant.activeStaff) },
    { key: 'classes', header: t('tenants.column.classes'), numeric: true, render: (tenant) => formatNumber(tenant.activeClasses) },
    { key: 'created', header: t('tenants.column.created'), render: (tenant) => formatDate(tenant.createdAt) },
  ];

  return (
    <>
      <div className="dg-page-header">
        <div className="dg-page-header__row">
          <h1>{t('tenants.title')}</h1>
          {canCreate && (
            <Button onClick={() => navigate('/tenants/new')} icon="＋">{t('tenants.new')}</Button>
          )}
        </div>
        <p className="dg-page-header__lede">{t('tenants.lede')}</p>
      </div>

      <Card>
        <Row>
          <label className="dg-field" style={{ flex: '1 1 260px' }}>
            <span className="dg-visually-hidden">{t('tenants.search')}</span>
            <input
              className="dg-input"
              type="search"
              placeholder={t('tenants.search')}
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            />
          </label>

          <label className="dg-field">
            <span className="dg-visually-hidden">{t('tenants.column.status')}</span>
            <Select
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(1); }}
              style={{ inlineSize: 'auto' }}
            >
              <option value="">{t('tenants.status.all')}</option>
              {STATUSES.map((option) => (
                <option key={option} value={option}>{t(`status.${option}`)}</option>
              ))}
            </Select>
          </label>
        </Row>
      </Card>

      <DataTable
        caption={t('tenants.caption')}
        columns={columns}
        rows={tenants.data?.items}
        rowKey={(tenant) => tenant.id}
        isLoading={tenants.isPending}
        error={tenants.error}
        onRetry={() => void tenants.refetch()}
        empty={<EmptyState icon="🏫" title={t('tenants.empty')} />}
      />

      {tenants.data && tenants.data.totalPages > 1 && (
        <Row between>
          <Button
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            {t('action.previous')}
          </Button>
          <span>{formatNumber(page)} / {formatNumber(tenants.data.totalPages)}</span>
          <Button
            variant="secondary"
            disabled={page >= tenants.data.totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            {t('action.next')}
          </Button>
        </Row>
      )}
    </>
  );
}
